import { Alert, Box, Button, CircularProgress, Link, Stack, TextField, Typography } from '@mui/material';
import { MarkEmailReadOutlined } from '@mui/icons-material';
import type { LoginOtpState } from './useLoginForm';

/**
 * Sign-in second step: the 6-digit code emailed after the password was accepted. Tokens are issued only
 * when it is verified. Resend after the countdown; "Use a different account" goes back to email and password.
 * Shared by every sign-in design; <paramref name="dark"/> suits the photo (Skyline) design.
 */
export default function LoginOtpPanel({ otp, dark = false, accent = '#2563EB' }: { otp: LoginOtpState; dark?: boolean; accent?: string }) {
  const text = dark ? '#E2E8F0' : '#0F172A';
  const muted = dark ? 'rgba(203,213,225,0.8)' : '#64748B';
  const link = dark ? '#93B4FF' : accent;

  return (
    <Box component="form" onSubmit={otp.verify} noValidate>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <MarkEmailReadOutlined sx={{ color: link, mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontWeight: 700, color: text }}>Check your email</Typography>
            <Typography variant="body2" sx={{ color: muted }}>{otp.message || `We sent a 6-digit code to ${otp.email}.`}</Typography>
          </Box>
        </Stack>

        <TextField
          value={otp.code} onChange={(e) => otp.setCode(e.target.value)} autoFocus fullWidth placeholder="••••••"
          error={!!otp.error}
          slotProps={{
            htmlInput: {
              inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6, 'aria-label': 'Verification code',
              style: { textAlign: 'center', fontSize: 26, letterSpacing: '0.6em', fontWeight: 700, paddingLeft: '0.6em' },
            },
          }}
          sx={dark ? {
            '& .MuiOutlinedInput-root': { bgcolor: 'rgba(15,23,42,0.45)', color: '#fff', borderRadius: '14px',
              '& fieldset': { borderColor: 'rgba(148,163,184,0.35)' }, '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.6)' } },
          } : undefined}
        />

        {otp.error && (
          <Alert severity="error" sx={dark ? { bgcolor: 'rgba(127,29,29,0.35)', color: '#FECACA', border: '1px solid rgba(248,113,113,0.4)', '& .MuiAlert-icon': { color: '#FCA5A5' } } : undefined}>
            {otp.error}
          </Alert>
        )}

        <Button type="submit" fullWidth size="large" variant="contained" disabled={otp.busy || otp.code.length !== 6}
          sx={{ height: 50, fontWeight: 700, textTransform: 'none', borderRadius: dark ? '16px' : 2,
            background: dark ? 'linear-gradient(90deg,#4F46E5,#2563EB 55%,#3B82F6)' : accent,
            '&:hover': { background: dark ? 'linear-gradient(90deg,#4F46E5,#2563EB 55%,#3B82F6)' : accent, filter: 'brightness(1.07)' },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.85)', background: dark ? 'linear-gradient(90deg,#4F46E5,#2563EB)' : accent, opacity: 0.6 } }}>
          {otp.busy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Verify and sign in'}
        </Button>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Link component="button" type="button" variant="body2" underline="hover" onClick={otp.back} sx={{ color: link, fontWeight: 600 }}>
            Use a different account
          </Link>
          {otp.resendIn > 0
            ? <Typography variant="body2" sx={{ color: muted }}>Resend code in {otp.resendIn}s</Typography>
            : <Link component="button" type="button" variant="body2" underline="hover" onClick={otp.resend} disabled={otp.busy} sx={{ color: link, fontWeight: 600 }}>Resend code</Link>}
        </Stack>
        <Typography variant="caption" sx={{ color: muted }}>The code is valid for 10 minutes. Check spam if it has not arrived.</Typography>
      </Stack>
    </Box>
  );
}
